import React from 'react';
import { gql } from '@apollo/client';
import { Link } from '@gsa/afp-component-library';
import { emDashUnicode } from '../../../utilities/constants';
import { getISODateStr } from '../components/contract-helpers';

export const GET_CONTRACT_HEADER_HISTORY = gql`
  query getContractHeaderHistory($contractHeaderId: String!) {
    getContractHeaderHistory(contractHeaderId: $contractHeaderId) {
      id
      versionNumber
      createdBy
      createdByUser {
        fullName
        email
      }
      createdAt
      changes
      versionUpdates {
        id
        changes
        createdBy
        createdAt
        createdByUser {
          fullName
          email
        }
      }
    }
  }
`;

export const formatDollar = (value) => {
  const f = parseFloat(value);
  if (Number.isNaN(f)) return emDashUnicode;
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(f);
};

export const formatBoolean = (value) => {
  if (typeof value !== 'boolean') return emDashUnicode;
  return value ? 'Yes' : 'No';
};

export const getVendorLink = (vendorId) => {
  if (!vendorId) return emDashUnicode;
  return (
    <Link href={`${window.AFP_CONFIG.appURLs.home}/vendor/details/${vendorId}`}>
      {vendorId}
    </Link>
  );
};

export const HeaderFieldList = [
  // version fields
  { key: 'contractYear', label: 'Contract year' },
  { key: 'vendorId', label: 'Vendor' },
  { key: 'contractPeriod', label: 'Period' },
  { key: 'ueiContractor', label: 'Contractor UEI' },
  { key: 'contractingOfficerName', label: 'Contracting officer name' },
  { key: 'contractingOfficerEmail', label: 'Contracting officer email' },
  { key: 'contractSpecialistName', label: 'Contract specialist name' },
  { key: 'contractSpecialistEmail', label: 'Contract specialist email' },
  { key: 'awardCancelledDate', converter: getISODateStr },
  { key: 'contractEndDate', converter: getISODateStr },
  {
    key: 'estimatedValue',
    label: 'Contract maximum value',
    converter: formatDollar,
  },
  { key: 'contractUnits', label: 'Contract units' },
  // update fields
  {
    key: 'publishedInFleet',
    converter: formatBoolean,
  },
  // hidden fields
  // { key: 'solicitationPeriodId', isHidden: true },
];

export const getFieldValue = (value, converter) => {
  if (value == null) return emDashUnicode;
  return converter ? converter(value) : value;
};

export const getLabel = (key) => {
  return (
    key[0].toUpperCase() +
    key.substring(1).replace(/[A-Z]/g, (c) => ` ${c.toLowerCase()}`)
  );
};
export const changeMapFn = (change) => {
  const field = HeaderFieldList.find((f) => f.key === change.fieldName);
  if (field) {
    if (field.isHidden) return null;
    return {
      fieldName: field.label ?? getLabel(field.key),
      previousValue: getFieldValue(change.previous, field.converter),
      currentValue: getFieldValue(change.current, field.converter),
    };
  }
  return {
    fieldName: getLabel(change.fieldName),
    previousValue: getFieldValue(change.previous),
    currentValue: getFieldValue(change.current),
  };
};
export const processChanges = (changes) => {
  const versionChanges = changes
    .filter((c) => c.changeType === 'v')
    .map(changeMapFn)
    .filter((c) => c !== null);
  const updateChanges = changes
    .filter((c) => c.changeType === 'u')
    .map(changeMapFn)
    .filter((c) => c !== null);
  return { versionChanges, updateChanges };
};

export const processHeaderVersions = (versions) => {
  return versions.map(
    ({
      versionNumber,
      changes,
      versionUpdates,
      createdAt,
      createdBy,
      createdByUser,
    }) => {
      const { versionChanges, updateChanges } = processChanges(changes);
      const updates = updateChanges.map((u) => ({
        ...u,
        updatedBy: createdByUser?.fullName ?? createdBy,
        updatedByEmail: createdByUser?.email,
        updatedAt: createdAt,
      }));

      [...versionUpdates]
        .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
        .forEach((vu) => {
          const { updateChanges: ucs } = processChanges(vu.changes);
          updates.unshift(
            ...ucs.map((uc) => ({
              ...uc,
              updatedBy: vu.createdByUser?.fullName ?? vu.createdBy,
              updatedByEmail: vu.createdByUser?.email,
              updatedAt: vu.createdAt,
            })),
          );
        });

      return {
        versionNumber,
        changedBy: createdByUser?.fullName ?? createdBy,
        changedByEmail: createdByUser?.email,
        changedAt: createdAt,
        changeCount: versionChanges.length + updates.length,
        changes: versionChanges,
        updates,
      };
    },
  );
};

export const filterVersionDate = (fromDate, toDate, { changedAt, updates }) => {
  const getDateStr = (str) => str.substring(0, 10);
  const getFilterDate = (d) =>
    d && !Number.isNaN(Date.parse(d))
      ? getDateStr(new Date(d).toISOString())
      : null;
  const fromStr = getFilterDate(fromDate);
  const toStr = getFilterDate(toDate);
  const changeStr = getDateStr(changedAt);

  if (!fromStr && !toStr) return true;
  if (fromStr && toStr) {
    return (
      (changeStr >= fromStr && changeStr <= toStr) ||
      updates.some((c) => {
        const cStr = getDateStr(c.updatedAt);
        return cStr >= fromStr && cStr <= toStr;
      })
    );
  }
  if (fromStr)
    return (
      changeStr >= fromStr ||
      updates.some((c) => getDateStr(c.updatedAt) >= fromStr)
    );
  return (
    changeStr <= toStr || updates.some((c) => getDateStr(c.updatedAt) <= toStr)
  );
};

export const filterVersionChangeField = (filterValue, field, v) =>
  !filterValue ||
  v.changes.some((c) => c[field] === filterValue) ||
  v.updates.some((c) => c[field] === filterValue);

export const filterAndSortVersions = (
  contractHeaderVersions,
  filters,
  order,
) => {
  const field = order[0];
  const asc = order[1] === 'ASC' ? -1 : 1;
  return contractHeaderVersions
    .filter(
      (v) =>
        (!filters.versionNumber ||
          v.versionNumber === +filters.versionNumber) &&
        (!filters.changedBy ||
          v.changedBy === filters.changedBy ||
          v.updates.some((c) => c.updatedBy === filters.changedBy)) &&
        filterVersionChangeField(filters.fieldName, 'fieldName', v) &&
        filterVersionDate(filters.changedAtFrom, filters.changedAtTo, v),
    )
    .sort((a, b) => (a[field] < b[field] ? asc : -asc));
};

export const getFilterOptions = (allVersions, field) => {
  const values = new Set();
  allVersions.forEach((v) => {
    if (field === 'versionNumber') {
      values.add(v.versionNumber);
    } else if (field === 'changedBy') {
      values.add(v.changedBy);
      v.updates.forEach((c) => values.add(c.updatedBy));
    } else if (field === 'fieldName') {
      v.changes.forEach((c) => values.add(c.fieldName));
      v.updates.forEach((c) => values.add(c.fieldName));
    }
  });
  return [
    { label: '-Select-', value: '' },
    ...[...values]
      .sort((a, b) => (a < b ? -1 : 1))
      .map((v) => ({ label: v, value: v })),
  ];
};
